import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import tripService from '../services/tripService';
import TripForm from '../components/trips/TripForm';
import ExpenseEntry from '../components/trips/ExpenseEntry';
import TripSummary from '../components/trips/TripSummary';
import Modal from '../components/common/Modal';
import Table from '../components/common/Table';
import Badge from '../components/common/Badge';
import PageHeader from '../components/common/PageHeader';
import { formatDate, formatCurrency } from '../utils/formatters';
import { useVehicles } from '../hooks/useVehicles';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

export default function Trips() {
  const [trips, setTrips]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState({ open: false, trip: null });
  const [declareModal, setDeclareModal] = useState({ open: false, trip: null });
  const [expenses, setExpenses] = useState([]);
  const [declaring, setDeclaring] = useState(false);
  const { selectedVehicle, handleDriverScopeError } = useVehicles();
  const { user } = useAuth();
  const isDriver = user?.role === 'driver';

  const load = async () => {
    setLoading(true);
    try {
      const params = isDriver ? {} : { vehicle: selectedVehicle?.id };
      setTrips(await tripService.getAll(params));
    } catch (err) {
      if (!isDriver || !handleDriverScopeError(err)) {
        setTrips([]);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [selectedVehicle, isDriver]);

  const openDeclare = (trip) => {
    setDeclareModal({ open: true, trip });
    const existing = (trip.expenses || []).map((e, i) => ({
      id: e.id || Date.now() + i,
      category: e.category,
      description: e.description || '',
      amount: e.amount,
    }));
    setExpenses(existing);
  };

  const submitDeclaration = async () => {
    setDeclaring(true);
    try {
      await tripService.declare(declareModal.trip.id, { expenses });
      toast.success(declareModal.trip?.status === 'completed' ? 'Declaration updated' : 'Declaration submitted — profit calculated');
      setDeclareModal({ open: false, trip: null });
      load();
    } catch (err) {
      if (isDriver && handleDriverScopeError(err)) {
        return;
      }
      toast.error('Failed to submit declaration');
    }
    finally { setDeclaring(false); }
  };

  const columns = [
    { key: 'date', label: 'Date', render: (v) => formatDate(v) },
    ...(isDriver ? [] : [{ key: 'vehicle_reg', label: 'Vehicle' }]),
    { key: 'route', label: 'Route' },
    { key: 'declared_amount', label: 'Revenue', render: (v) => <span className="mono-number">{formatCurrency(v)}</span>, tdStyle: { textAlign: 'right' } },
    { key: 'total_expenses', label: 'Expenses', render: (v) => <span className="mono-number">{formatCurrency(v)}</span>, tdStyle: { textAlign: 'right' } },
    { key: 'profit', label: 'Profit', render: (v) => (
      <span className="mono-number" style={{ color: v >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>{formatCurrency(v)}</span>
    ), tdStyle: { textAlign: 'right' } },
    { key: 'status', label: 'Status', render: (v) => <Badge status={v} label={v} /> },
    { key: 'id', label: '', render: (v, row) => (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {!isDriver && <Link to={`/trips/${v}`} className="btn btn-secondary btn-sm">View</Link>}
        <button className="btn btn-primary btn-sm" onClick={() => openDeclare(row)}>{row.status === 'completed' ? 'Edit Declaration' : 'Declare'}</button>
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader
        title="Trips"
        subtitle={isDriver ? 'Today only: trip records and declarations' : 'Daily trip records and declarations'}
        actions={<button className="btn btn-primary" onClick={() => setModal({ open: true, trip: null })}>+ New Trip</button>}
      />
      <div className="card">
        <Table columns={columns} data={trips} loading={loading} emptyMessage="No trips recorded yet" />
      </div>

      <Modal isOpen={modal.open} onClose={() => setModal({ open: false, trip: null })} title="Record New Trip">
        <TripForm onCancel={() => setModal({ open: false, trip: null })}
          onSuccess={() => { setModal({ open: false, trip: null }); load(); }} />
      </Modal>

      <Modal isOpen={declareModal.open} onClose={() => setDeclareModal({ open: false, trip: null })}
        title={declareModal.trip?.status === 'completed' ? 'Edit Trip Declaration' : 'Declare Trip Expenses'} size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setDeclareModal({ open: false, trip: null })}>Cancel</button>
            <button className="btn btn-primary" onClick={submitDeclaration} disabled={declaring}>
              {declaring ? 'Submitting…' : 'Submit Declaration'}
            </button>
          </>
        }>
        <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
          Trip: <strong>{declareModal.trip?.route}</strong> on {declareModal.trip?.date && formatDate(declareModal.trip.date)}
        </p>
        <ExpenseEntry expenses={expenses} onChange={setExpenses} />
        <TripSummary declaredAmount={declareModal.trip?.declared_amount} expenses={expenses} />
      </Modal>
    </div>
  );
}